/**
 * What to do about a ring — and about a leave — as a decision rather than a
 * chain of conditions.
 *
 * The three rules in `relay/docs/protocol.md` §7b are two lines of prose each
 * and four branches of code, and the branches are the kind that are read once,
 * agreed with, and then quietly get an extra condition. So they live here, with
 * no React, no store and no socket in sight — the same reason
 * `components/call/controls.ts` exists.
 *
 * `state/calls.tsx` is the one caller.
 */

import type { ConversationKind, Presence } from '../lib/relay/types';

/** Ring it, refuse it, or let it rain. */
export type RingAction = 'ring' | 'decline' | 'ignore';

export function ringAction(situation: {
  /** A call this browser is already in, or already being offered. */
  engaged: boolean;
  /** The conversation the ring arrived in. Unknown for a conversation this
   *  page has not loaded yet, which reads as "not a DM". */
  kind: ConversationKind | undefined;
  /** This account's own presence. `busy` is what do-not-disturb folds onto. */
  presence: Presence;
}): RingAction {
  // Already in one. A DM still declines — the other end is one person waiting
  // for an answer, and silence is not one. A group does not: its call bar is
  // the invitation, and a decline from somebody mid-call is noise.
  if (situation.engaged) return situation.kind === 'direct' ? 'decline' : 'ignore';

  // Do not disturb: nothing seen, nothing heard, nothing sent. The caller's own
  // timeout writes it as missed, which is what happened.
  if (situation.presence === 'busy') return 'ignore';

  return 'ring';
}

/**
 * Whether an outgoing call is over because everybody said no.
 *
 * A decline is a fact, not an ending — until it is the last one. In a DM the
 * first is the last, which is the old rule falling out of the new one rather
 * than being written twice.
 *
 * An empty `rung` is a conversation whose membership this page never learnt,
 * and there the one answer anybody gave is the whole of what is known: a call
 * that would otherwise ring on for thirty seconds after being refused.
 */
export function everybodyDeclined(rung: ReadonlySet<string>, declined: ReadonlySet<string>): boolean {
  if (rung.size === 0) return declined.size > 0;
  for (const id of rung) if (!declined.has(id)) return false;
  return true;
}

/** What somebody else's `leave` does to the call this browser holds. */
export type LeaveEffect = 'stopRinging' | 'hangUp' | 'nothing';

/**
 * An invitation stops: a caller who gave up is not still calling. In a DM the
 * call ends, whichever end placed it — there is one other seat, and it just
 * emptied. In a group it is a call of six becoming a call of five.
 */
export function leaveEffect(situation: {
  ringing: boolean;
  kind: ConversationKind | undefined;
}): LeaveEffect {
  if (situation.ringing) return 'stopRinging';
  return situation.kind === 'direct' ? 'hangUp' : 'nothing';
}
